import { gql } from "@apollo/client";
import { PageContentStack } from "atoms/Stack";
import { gaSetSharedEventParams } from "helpers/gtag";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import Message from "molecules/Message";
import React, { useEffect, useState } from "react";
import PageTitle from "shared/PageTitle";
import UnauthorisedPage from "shared/UnauthorisedPage";

import BoxScore, { BoxScoreFragment, BoxScoreGamePlayerFragment } from "./games/BoxScore";
import GameOverviewHighlights, {
  GameOverviewHighlightsFragment,
  GameOverviewHighlightsGamePlayerFragment,
} from "./games/GameOverviewHighlights";
import GameOverviewPageHeader, {
  GameOverviewPageHeaderFragment,
  GameOverviewPageHeaderGamePlayerFragment,
} from "./games/GameOverviewPageHeader";
import GameOverviewReels, {
  GameOverviewReelsFragment,
  GameOverviewReelsGamePlayerFragment,
} from "./games/GameOverviewReels";
import UpdateStubbedUserEmailModal from "./games/GameOverviewUpdateStubbedUserEmailModal";
import PointChart from "./games/PointChart";
import ShotChart from "./games/ShotChart";

const GameOverviewPageGamePlayerFragment = gql`
  fragment GameOverviewPageGamePlayerFragment on GamePlayer {
    id
    ...GameOverviewPageHeaderGamePlayerFragment
    ...GameOverviewReelsGamePlayerFragment
    ...GameOverviewHighlightsGamePlayerFragment
    ...BoxScoreGamePlayerFragment
  }
  ${GameOverviewPageHeaderGamePlayerFragment}
  ${GameOverviewReelsGamePlayerFragment}
  ${GameOverviewHighlightsGamePlayerFragment}
  ${BoxScoreGamePlayerFragment}
`;

export default function GameOverviewPage({ gameId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [gamePlayerId, gamePlayerIdSet] = useRouteState("gamePlayerId");
  const [tab, tabSet] = useRouteState("tab");
  const [stubbedModalOpen, setStubbedModalOpen] = useState(false);
  const [currentUserData] = useData(gql`
    query GameOverviewPage_current_user {
      currentUser {
        id
        email
        stubbed
      }
    }
  `);
  const [permissionData] = useData(
    gql`
      query GameOverviewPage_permission($gameId: ID!) {
        game(id: $gameId) {
          id
          canOnsenAccessVideo
        }
      }
    `,
    { gameId },
  );
  const [data] = useData(
    gql`
      query GameOverviewPage($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
          }
          org {
            gaId
          }
          statsVisible
          ...GameOverviewPageHeaderFragment
          ...GameOverviewReelsFragment
          ...GameOverviewHighlightsFragment
          ...BoxScoreFragment
        }
      }
      ${GameOverviewPageHeaderFragment}
      ${GameOverviewReelsFragment}
      ${GameOverviewHighlightsFragment}
      ${BoxScoreFragment}
    `,
    { gameId },
    { skip: permissionData && !permissionData.game?.canOnsenAccessVideo },
  );
  gaSetSharedEventParams({ org_id: data?.game?.org?.gaId });

  const [dataDefaultGamePlayer] = useData(
    gql`
      query GameOverviewPage_defaultGamePlayer($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
            ...GameOverviewPageGamePlayerFragment
          }
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gameId },
    { skip: !!gamePlayerId },
  );

  const [dataGamePlayer] = useData(
    gql`
      query GameOverviewPage_gamePlayer($gamePlayerId: ID!) {
        gamePlayer(id: $gamePlayerId) {
          id
          ...GameOverviewPageGamePlayerFragment
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gamePlayerId },
    { skip: !gamePlayerId },
  );

  const gamePlayer = dataGamePlayer?.gamePlayer || dataDefaultGamePlayer?.game?.currentUserDefaultGamePlayer;

  useEffect(() => {
    if (currentUserData?.currentUser?.stubbed === true) {
      setStubbedModalOpen(true);
    }
  }, [currentUserData?.currentUser?.stubbed]);

  if (permissionData && !permissionData.game?.canOnsenAccessVideo) {
    return <UnauthorisedPage currentUser={currentUserData?.currentUser} />;
  }

  return (
    <>
      <PageTitle title="Game Overview" />
      <UpdateStubbedUserEmailModal open={stubbedModalOpen} onClose={() => setStubbedModalOpen(false)} />
      <GameOverviewPageHeader
        game={data?.game}
        gamePlayer={gamePlayer}
        gamePlayerIdSet={gamePlayerIdSet}
        tab={tab}
        tabSet={tabSet}
      />
      {data?.game?.statsVisible && (
        <PageContentStack>
          <>
            {gamePlayer && (
              <>
                {(!tab || tab === "reels") && <GameOverviewReels game={data?.game} gamePlayer={gamePlayer} />}
                {(!tab || tab === "highlights") && (
                  <GameOverviewHighlights game={data?.game} gameId={gameId} gamePlayer={gamePlayer} />
                )}
                {(!tab || tab === "shotchart") && <ShotChart gamePlayerId={gamePlayer?.id} />}
              </>
            )}
          </>
          {(!tab || tab === "boxscore") && <BoxScore game={data?.game} gamePlayer={gamePlayer} />}
          {(!tab || tab === "pointchart") && <PointChart gameId={gameId} />}
        </PageContentStack>
      )}

      {!data?.game?.statsVisible && (
        <PageContentStack>
          <Message>Let’s watch the game! You can add more reactions as you watch each moment.</Message>
        </PageContentStack>
      )}
    </>
  );
}
